import { useQuery } from '@tanstack/react-query'

import { apiClient } from '@/lib/api-client'

import type { EnvelopeStatusFilter } from '../envelopes.enums'
import { isSignerBasedStatus } from '../envelopes.enums'

import type { ListEnvelopesResponse } from './list-envelopes.types'

type OwnerFilter = 'account' | 'current-user' | string

interface ListEnvelopesParams {
  search?: string
  vaultIds?: Array<string>
  pageSize?: number
  pageNumber?: number
  orderByDirection?: 'ASC' | 'DESC'
}

function buildEndpoint(
  statusFilter: EnvelopeStatusFilter,
  ownerFilter: OwnerFilter,
): string {
  if (isSignerBasedStatus(statusFilter)) {
    return `/v1/ecm/envelopes/list/${statusFilter}`
  }

  const statusSegment = statusFilter === 'all' ? 'all' : statusFilter

  if (ownerFilter === 'account') {
    return `/v1/ecm/envelopes/list/${statusSegment}/accounts`
  } else if (ownerFilter === 'current-user') {
    return `/v1/ecm/envelopes/list/${statusSegment}/user-accounts`
  } else {
    return `/v1/ecm/envelopes/list/${statusSegment}/user-accounts/${ownerFilter}`
  }
}

const listEnvelopes = async (
  statusFilter: EnvelopeStatusFilter,
  ownerFilter: OwnerFilter,
  params?: ListEnvelopesParams,
): Promise<ListEnvelopesResponse> => {
  const endpoint = buildEndpoint(statusFilter, ownerFilter)
  const isSigner = isSignerBasedStatus(statusFilter)

  const response = await apiClient.get<ListEnvelopesResponse>(endpoint, {
    params: {
      Search: params?.search || undefined,
      VaultIds:
        !isSigner && params?.vaultIds?.length ? params.vaultIds : undefined,
      PageSize: params?.pageSize ?? 5,
      PageNumber: params?.pageNumber ?? 1,
      OrderByDirection: params?.orderByDirection ?? 'DESC',
    },
  })
  return response.data
}

export const useListEnvelopes = (
  statusFilter: EnvelopeStatusFilter = 'all',
  ownerFilter: OwnerFilter = 'current-user',
  params?: ListEnvelopesParams,
) => {
  return useQuery({
    queryKey: ['envelopes', statusFilter, ownerFilter, params],
    queryFn: () => listEnvelopes(statusFilter, ownerFilter, params),
  })
}
